<script setup lang="ts">
import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props.js'
import { CART_FEEDBACK_YOU_MAY_ALSO_LIKE_IDS } from '@integration-layer/data/xGenIds'

const emit = defineEmits<{
  'open-quick-buy': [productCardHorizontal: ProductCardHorizontalProps]
}>()

// Get lastAddedToCart to trigger refresh when it changes
const { lastAddedToCart } = useAddToCart()

// Create a reactive key that changes when lastAddedToCart changes
const refreshKey = computed(
  () =>
    `${lastAddedToCart.value?.id || 'initial'}-${lastAddedToCart.value?.skuCode || ''}`
)

// Watch for changes in lastAddedToCart and refresh recommendations
if (import.meta.client) {
  watch(
    lastAddedToCart,
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue) {
        // Clear the cache for this specific XGen recommendation
        clearNuxtData(
          `xgen-recommendations-${CART_FEEDBACK_YOU_MAY_ALSO_LIKE_IDS}`
        )
      }
    },
    { deep: true }
  )
}
const desktopAlignment = () => 0.6 // Avoids Embla issue where "start" + loop skips prev slide at start
</script>

<template>
  <DelayHydration>
    <div>
      <h3 class="text-book-4 text-text-primary mb-6 mt-10 uppercase">
        {{ $ts('cart.relatedProduct') }}
      </h3>

      <XGenRecommendations
        :key="refreshKey"
        :prediction="CART_FEEDBACK_YOU_MAY_ALSO_LIKE_IDS"
        :initial-cl-fetch="10"
      >
        <template #default="{ products }">
          <UISliderWithArrows
            v-if="products && products.length > 0"
            :id="`XSE-CART_FEEDBACK_YOU_MAY_ALSO_LIKE_IDS-${refreshKey}`"
            class="XGen_SmartElement"
            :items="products"
            :desktop-alignment="desktopAlignment"
            :hide-lateral-slides-on-mobile="true"
          >
            <template #default="item">
              <WrapperProductTile :product="item">
                <template #default="{ product, productCardHorizontal }">
                  <UIProductTile
                    xse-type="rec"
                    :xse-prod-code="product.sku"
                    :product="{
                      ...product,
                      linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${CART_FEEDBACK_YOU_MAY_ALSO_LIKE_IDS}`,
                    }"
                    disable-swipe-mobile
                    @open-quick-buy="
                      () =>
                        productCardHorizontal &&
                        emit('open-quick-buy', productCardHorizontal)
                    "
                  />
                </template>
              </WrapperProductTile>
            </template>
          </UISliderWithArrows>
        </template>
      </XGenRecommendations>
    </div>
  </DelayHydration>
</template>

<style scoped lang="scss">
:deep(.slider-base-container) {
  margin: 0;
}
:deep(.slider-base-container .embla) {
  overflow: visible;
}
:deep(
    .slider-base-container .navigation-buttons-container .navigation-button-prev
  ) {
  left: 0;
}
:deep(
    .slider-base-container .navigation-buttons-container .navigation-button-next
  ) {
  right: 0;
}
:deep(.navigation-button-prev .navigation-button),
:deep(.navigation-button-next .navigation-button) {
  transform: unset;
  width: var(--positive-padding-x);
}
:deep(.navigation-button-prev .navigation-button) {
  left: calc(var(--positive-padding-x) * -1);
}

:deep(.navigation-button-next .navigation-button) {
  right: calc(var(--positive-padding-x) * -1);
}
@media (min-width: 1024px) {
  :deep(.embla) {
    margin: 0;
  }
}
</style>
